import { useState, memo, useMemo } from 'react'

import { PlaybookType } from 'app/types'

import { useBoolean, usePlaylistsHttp } from 'hooks'

import { ShareCommonDialog } from './ShareCommonDialog'

type Props = {
    isOpen?: boolean
    selectedPlaybooks: Array<PlaybookType>
    onClose: () => void
    searchRefresh?: () => void
    isSearchRevalidating?: boolean
    singleMode?: boolean
}

export const ShareToPlaylist = memo(
    ({ isOpen = true, selectedPlaybooks, onClose, searchRefresh = () => {} }: Props) => {
        const [title, setTitle] = useState('')

        const creating = useBoolean()

        const selectedPlaybooksIds = selectedPlaybooks
            .filter(({ isPlaylist }) => !isPlaylist)
            .map(({ id }) => id)

        const createNew = useBoolean()
        const loading = useBoolean()

        const {
            addToPlaylists,
            removeFromPlaylists,
            isPlaylistsLoading,
            playlistsOptions,
            createPlaylist
        } = usePlaylistsHttp(selectedPlaybooksIds)

        const handleCreate = () => {
            createPlaylist(title, selectedPlaybooksIds).then(() => {
                createNew.setFalse()
                searchRefresh()
                onClose()
            })
        }

        const handleItemClick = async (id: string, wasSelected: boolean) => {
            // for multiselect we don't allow to remove videos from playlist, only adding
            if (!wasSelected) {
                await addToPlaylists([id])
            } else {
                await removeFromPlaylists([id])
            }

            searchRefresh()
        }

        const options = useMemo(() => {
            return playlistsOptions.map(({ playlist, isSelected }) => ({
                id: playlist.id,
                name: playlist.title,
                visibility: playlist.visibility,
                isSelected
            }))
        }, [playlistsOptions])

        return (
            <ShareCommonDialog
                label="New Playlist"
                header="Save to playlist"
                buttonLabel="Add new Playlist"
                showCreateButton={creating.isTrue}
                title={title}
                setTitle={setTitle}
                onAddClick={creating.setTrue}
                onCreateClick={handleCreate}
                onItemClick={handleItemClick}
                disableCreate={title.length < 1}
                isLoading={loading.isTrue || isPlaylistsLoading}
                onClose={() => {
                    onClose()
                    setTitle('')
                    creating.setFalse()
                }}
                isOpen={isOpen}
                options={options}
            />
        )
    }
)
